import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  List,
  ListItem,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  TextField as MuiTextField,
} from '@mui/material';
import {
  MdCheck,
  MdCheckCircle,
  MdError,
  MdUpload,
  MdEdit,
} from '@teamworksdev/icons';

import { useState } from 'react';
import { PageHeader } from '../../../components/layout/page-header';
import { trpc } from '../../../../trpc/react';
import {
  ImportRemovalMode,
  Org,
  ParsedExercise,
  RemovalMode,
  RemovalPreviewExercise,
  Team,
} from './types';
import {
  filterExercisesByDefault,
  filterExercisesByName,
  filterExercisesByScope,
} from './utils';
import type { DefaultExerciseFilter } from './utils';
import { useExerciseLibraryManagement } from './useExerciseLibraryManagement';
import { TypeToConfirmDialog } from './TypeToConfirmDialog';

/** Above this count, replace preview collapses to a count-only summary. */
const LARGE_LIBRARY_THRESHOLD = 1000;

const IMPORT_MODE_GROUP_LABEL_ID = 'import-removal-mode-label';
const REPLACE_DISCLOSURE_ID = 'import-removal-mode-replace-description';

interface ToolbarProps {
  orgs: Org[] | undefined;
  isLoading: boolean;
  selectedOrgId: string | null;
  onOrgChange: (orgId: string | null) => void;
  selectedTeamId: string | null;
  onTeamChange: (teamId: string | null) => void;
  teams: Team[] | undefined;
  /** Only orgs with team libraries enabled offer team scoping. */
  isTeamLibraryEnabled: boolean;
}

function Toolbar({
  orgs,
  isLoading,
  selectedOrgId,
  onOrgChange,
  selectedTeamId,
  onTeamChange,
  teams,
  isTeamLibraryEnabled,
}: ToolbarProps) {
  return (
    <Box className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <Autocomplete
        options={orgs || []}
        getOptionLabel={(option: Org) => option.name}
        value={orgs?.find((org) => org.id === selectedOrgId) || null}
        onChange={(_, newValue) => onOrgChange(newValue?.id || null)}
        renderInput={(params) => (
          <MuiTextField
            {...params}
            label="Organization"
            placeholder="Select an organization"
          />
        )}
        loading={isLoading}
        className="w-full sm:w-72"
      />
      {isTeamLibraryEnabled && (
        <Autocomplete
          options={teams || []}
          getOptionLabel={(option: Team) => option.name}
          value={teams?.find((team) => team.id === selectedTeamId) || null}
          onChange={(_, newValue) => onTeamChange(newValue?.id || null)}
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label="Team (Optional)"
              placeholder="Base Library"
            />
          )}
          disabled={!selectedOrgId}
          className="w-full sm:w-72"
        />
      )}
    </Box>
  );
}

interface ValidationSummaryProps {
  total: number;
  createCount: number;
  updateCount: number;
  skipCount: number;
  errorCount: number;
  hasErrors: boolean;
}

function ValidationSummary({
  total,
  createCount,
  updateCount,
  skipCount,
  errorCount,
  hasErrors,
}: ValidationSummaryProps) {
  return (
    <>
      <Box className="flex gap-4 mb-4 flex-wrap">
        <Alert severity="info" className="flex-1 min-w-[8rem]">
          <strong>{total}</strong> total exercises
        </Alert>
        {createCount > 0 && (
          <Alert severity="success" className="flex-1 min-w-[8rem]">
            <strong>{createCount}</strong> new
          </Alert>
        )}
        {updateCount > 0 && (
          <Alert severity="info" className="flex-1 min-w-[8rem]">
            <strong>{updateCount}</strong> updates
          </Alert>
        )}
        {skipCount > 0 && (
          <Alert severity="success" className="flex-1 min-w-[8rem]">
            <strong>{skipCount}</strong> no changes
          </Alert>
        )}
        {errorCount > 0 && (
          <Alert severity="error" className="flex-1 min-w-[8rem]">
            <strong>{errorCount}</strong> errors
          </Alert>
        )}
      </Box>

      {hasErrors && (
        <Alert severity="error" className="mb-4">
          <strong>{errorCount}</strong> exercise(s) have errors. Please fix the
          issues in your CSV file and re-upload to continue.
        </Alert>
      )}
    </>
  );
}

interface ExerciseTableRowProps {
  exercise: ParsedExercise;
}

function ExerciseTableRow({ exercise }: ExerciseTableRowProps) {
  const hasError = exercise.issues.some((i) => i.type === 'error');
  const hasNewEntities =
    exercise.missingEntities.types.length > 0 ||
    exercise.missingEntities.bodyParts.length > 0 ||
    exercise.missingEntities.equipment.length > 0 ||
    exercise.missingEntities.tags.length > 0;
  const errorMessages = exercise.issues.filter((i) => i.type === 'error');
  const warningMessages = exercise.issues.filter((i) => i.type === 'warning');

  const renderStatusChip = () => {
    switch (exercise.action) {
      case 'skip':
        return (
          <Chip
            label="Skip"
            size="small"
            color="success"
            icon={<MdCheckCircle />}
          />
        );
      case 'update':
        return (
          <Chip label="Update" size="small" color="info" icon={<MdEdit />} />
        );
      default:
        return (
          <Chip
            label="New"
            size="small"
            color="success"
            icon={<MdCheckCircle />}
          />
        );
    }
  };

  const renderNewEntities = () => {
    if (!hasNewEntities) return null;

    return (
      <Typography variant="body2" className="text-sm">
        {exercise.missingEntities.types.length > 0 && (
          <div>
            <strong>Type:</strong> {exercise.missingEntities.types.join(', ')}
          </div>
        )}
        {exercise.missingEntities.bodyParts.length > 0 && (
          <div>
            <strong>Body Parts:</strong>{' '}
            {exercise.missingEntities.bodyParts.join(', ')}
          </div>
        )}
        {exercise.missingEntities.equipment.length > 0 && (
          <div>
            <strong>Equipment:</strong>{' '}
            {exercise.missingEntities.equipment.join(', ')}
          </div>
        )}
        {exercise.missingEntities.tags.length > 0 && (
          <div>
            <strong>Tags:</strong> {exercise.missingEntities.tags.join(', ')}
          </div>
        )}
      </Typography>
    );
  };

  const renderFieldChange = (change: string, key: number) => {
    const colonIndex = change.indexOf(':');
    if (colonIndex > -1) {
      const fieldName = change.substring(0, colonIndex);
      const value = change.substring(colonIndex);
      return (
        <div key={key}>
          <strong>{fieldName}</strong>
          {value}
        </div>
      );
    }
    return <div key={key}>{change}</div>;
  };

  const renderIssues = () => {
    if (errorMessages.length > 0) {
      return (
        <Box className="text-sm">
          {errorMessages.map((issue, idx) => (
            <Box key={idx} className="flex items-start gap-1 mb-1">
              <MdError
                className="text-red-600 flex-shrink-0 mt-0.5"
                size={16}
              />
              <span className="text-red-600">{issue.message}</span>
            </Box>
          ))}
        </Box>
      );
    }

    if (exercise.changes && exercise.changes.length > 0) {
      return (
        <Typography variant="body2" className="text-sm text-gray-600">
          {exercise.changes.map((change, idx) =>
            renderFieldChange(change, idx),
          )}
        </Typography>
      );
    }

    if (warningMessages.length > 0) {
      return (
        <Typography variant="body2" className="text-sm text-gray-600">
          {warningMessages.map((issue, idx) => {
            const changes = issue.message.split('; ');
            return (
              <div key={idx}>
                {changes.map((change, changeIdx) =>
                  renderFieldChange(change, changeIdx),
                )}
              </div>
            );
          })}
        </Typography>
      );
    }

    return null;
  };

  return (
    <TableRow className={hasError ? 'bg-red-50' : ''}>
      <TableCell>{exercise.rowNumber}</TableCell>
      <TableCell>
        <Typography variant="body2" className="font-medium">
          {exercise.name}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip label={exercise.type} size="small" />
      </TableCell>
      <TableCell>{renderStatusChip()}</TableCell>
      <TableCell>{renderNewEntities()}</TableCell>
      <TableCell>{renderIssues()}</TableCell>
    </TableRow>
  );
}

interface ImportPreviewTableProps {
  exercises: ParsedExercise[];
  hasErrors: boolean;
}

function ImportPreviewTable({ exercises, hasErrors }: ImportPreviewTableProps) {
  const filteredExercises = hasErrors
    ? exercises.filter((e) => e.issues.some((i) => i.type === 'error'))
    : exercises.filter((e) => e.action !== 'skip');

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      className="max-h-[360px] overflow-auto"
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell width={60}>Row</TableCell>
            <TableCell width={300}>Name</TableCell>
            <TableCell width={150}>Type</TableCell>
            <TableCell width={100}>Status</TableCell>
            <TableCell width={250}>New Entries</TableCell>
            <TableCell>Issues</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredExercises.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center" className="py-8">
                <Typography variant="body1" className="text-gray-500">
                  No exercises to import. All exercises are unchanged.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredExercises.map((exercise) => (
              <ExerciseTableRow key={exercise.rowNumber} exercise={exercise} />
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

interface NotInListReplacePreviewProps {
  count: number;
  exercises: RemovalPreviewExercise[];
  isLoading: boolean;
}

function NotInListReplacePreview({
  count,
  exercises,
  isLoading,
}: NotInListReplacePreviewProps) {
  if (isLoading) {
    return (
      <Box className="flex justify-center py-4">
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (count === 0) {
    return (
      <Typography variant="body2" className="text-gray-600">
        No exercises will be removed — every existing exercise is named in the
        file.
      </Typography>
    );
  }

  const countLabel =
    count === 1
      ? '1 exercise will be removed'
      : `${count} exercises will be removed`;

  if (count >= LARGE_LIBRARY_THRESHOLD) {
    return (
      <Alert severity="warning">
        {countLabel}. Too many exercises to list individually — showing a
        count-only summary.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="body2" className="mb-2">
        {countLabel}
      </Typography>
      <List
        dense
        aria-label="Exercises that will be removed"
        sx={{ maxHeight: 240, overflow: 'auto' }}
        className="border border-gray-200 rounded"
      >
        {exercises.map((exercise) => (
          <ListItem key={exercise.id}>{exercise.name}</ListItem>
        ))}
      </List>
    </Box>
  );
}

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  file: File | null;
  isProcessing: boolean;
  selectedOrgId: string | null;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  validationComplete: boolean;
  exercises: ParsedExercise[];
  hasErrors: boolean;
  errorCount: number;
  createCount: number;
  updateCount: number;
  skipCount: number;
  hasExercisesToImport: boolean;
  isImporting: boolean;
  onImport: () => void;
  requireMetadata?: boolean;
  importRemovalMode: ImportRemovalMode | null;
  onImportRemovalModeChange: (mode: ImportRemovalMode) => void;
  notInListCandidates: RemovalPreviewExercise[];
  notInListCount: number;
  /** Team name when replace is team-scoped; null/undefined for org-wide. */
  selectedTeamName?: string | null;
  /** True only on the initial fetch — drives the checklist/preview spinner. */
  isPreviewNotInListLoading: boolean;
  /** True on the initial fetch and on every background refetch — keeps Continue disabled while candidates may be stale. */
  isPreviewNotInListFetching: boolean;
  onContinueToConfirm: () => void;
  /** True while type-to-confirm is open — blocks cancel/close of this dialog. */
  isConfirmDialogOpen: boolean;
  isRemoving: boolean;
}

function ImportDialog({
  open,
  onClose,
  fileInputRef,
  file,
  isProcessing,
  selectedOrgId,
  onFileSelect,
  validationComplete,
  exercises,
  hasErrors,
  errorCount,
  createCount,
  updateCount,
  skipCount,
  hasExercisesToImport,
  isImporting,
  onImport,
  requireMetadata,
  importRemovalMode,
  onImportRemovalModeChange,
  notInListCandidates,
  notInListCount,
  selectedTeamName,
  isPreviewNotInListLoading,
  isPreviewNotInListFetching,
  onContinueToConfirm,
  isConfirmDialogOpen,
  isRemoving,
}: ImportDialogProps) {
  const showModeSection = validationComplete && exercises.length > 0;
  const mode = importRemovalMode ?? 'import_only';
  const isImportOnly = mode === 'import_only';
  const dialogBusy = isImporting || isConfirmDialogOpen || isRemoving;

  const continueDisabled =
    !validationComplete ||
    hasErrors ||
    dialogBusy ||
    isPreviewNotInListFetching ||
    (mode === 'replace' && notInListCount === 0);

  const importOnlyDisabled =
    !validationComplete || !hasExercisesToImport || hasErrors || dialogBusy;

  const handleClose = () => {
    if (dialogBusy) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Exercises</DialogTitle>
      <DialogContent className="flex flex-col gap-4">
        <Typography variant="body2" className="text-gray-600">
          Required columns: name, type. Optional: body_parts, equipment,
          default_metrics, video_url, image_url, tags
        </Typography>

        {requireMetadata && (
          <Alert severity="warning">
            This organization requires exercise metadata fields (tags, body
            parts, equipment). Make sure your CSV includes these fields.
          </Alert>
        )}

        <Box className="flex items-center gap-4 flex-wrap">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={onFileSelect}
            style={{ display: 'none' }}
            id="csv-upload-dialog"
            disabled={!selectedOrgId || isProcessing}
          />
          <label htmlFor="csv-upload-dialog">
            <Button
              variant="contained"
              component="span"
              startIcon={
                isProcessing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <MdUpload />
                )
              }
              disabled={!selectedOrgId || isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Choose File'}
            </Button>
          </label>
          {file && !isProcessing && (
            <Typography variant="body2" className="text-gray-700">
              {file.name}
            </Typography>
          )}
        </Box>

        {showModeSection && (
          <>
            <ValidationSummary
              total={exercises.length}
              createCount={createCount}
              updateCount={updateCount}
              skipCount={skipCount}
              errorCount={errorCount}
              hasErrors={hasErrors}
            />
            <ImportPreviewTable exercises={exercises} hasErrors={hasErrors} />

            {!hasErrors && (
              <FormControl component="fieldset" className="mt-2">
                <FormLabel id={IMPORT_MODE_GROUP_LABEL_ID} component="legend">
                  After import
                </FormLabel>
                <RadioGroup
                  aria-labelledby={IMPORT_MODE_GROUP_LABEL_ID}
                  name="import-removal-mode"
                  value={mode}
                  onChange={(event) =>
                    onImportRemovalModeChange(
                      event.target.value as ImportRemovalMode,
                    )
                  }
                >
                  <FormControlLabel
                    value="import_only"
                    control={<Radio />}
                    label="Import only"
                  />
                  <Box>
                    <FormControlLabel
                      value="replace"
                      control={
                        <Radio
                          inputProps={{
                            'aria-describedby': REPLACE_DISCLOSURE_ID,
                          }}
                        />
                      }
                      label="Replace"
                    />
                    <Typography
                      id={REPLACE_DISCLOSURE_ID}
                      variant="body2"
                      className="text-gray-600 ml-8 mb-2"
                    >
                      {selectedTeamName
                        ? `In addition to importing this file, any of ${selectedTeamName}'s exercises not included in it will also be removed. Base library and other teams are left untouched.`
                        : 'In addition to importing this file, any existing exercises not included in it will also be removed — this reaches the whole organization (base library and every team).'}
                    </Typography>
                    {selectedTeamName && (
                      <Alert severity="warning" className="ml-8 mb-2">
                        Replace is scoped to {selectedTeamName} only. Exercises
                        in the base library and other teams will not be removed.
                      </Alert>
                    )}
                  </Box>
                </RadioGroup>
              </FormControl>
            )}

            {!hasErrors && mode === 'replace' && (
              <NotInListReplacePreview
                count={notInListCount}
                exercises={notInListCandidates}
                isLoading={isPreviewNotInListLoading}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={dialogBusy}>
          Cancel
        </Button>
        {isImportOnly ? (
          <Button
            variant="contained"
            onClick={onImport}
            disabled={importOnlyDisabled}
            startIcon={
              isImporting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <MdCheckCircle />
              )
            }
          >
            {isImporting
              ? 'Importing...'
              : `Import ${createCount + updateCount} Exercise${
                  createCount + updateCount !== 1 ? 's' : ''
                }`}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="error"
            onClick={onContinueToConfirm}
            disabled={continueDisabled}
          >
            Continue to confirm
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

interface ExistingExercisesTableProps {
  exercises: RemovalPreviewExercise[];
  isLoading: boolean;
  selectedTeamId: string | null;
  selectedExerciseIds: string[];
  onToggleExerciseSelection: (exerciseId: string) => void;
  onSetAllSelection: (exerciseIds: string[], selected: boolean) => void;
  onBulkDelete: () => void;
  onWipe: () => void;
  onImport: () => void;
  onExport: (exerciseIds: string[]) => void;
  isRemoving: boolean;
  removalError: string | null;
}

function ExistingExercisesTable({
  exercises,
  isLoading,
  selectedTeamId,
  selectedExerciseIds,
  onToggleExerciseSelection,
  onSetAllSelection,
  onBulkDelete,
  onWipe,
  onImport,
  onExport,
  isRemoving,
  removalError,
}: ExistingExercisesTableProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [defaultFilter, setDefaultFilter] =
    useState<DefaultExerciseFilter>('all');
  const [showBaseLibraryRows, setShowBaseLibraryRows] = useState(true);
  const visible = filterExercisesByDefault(
    filterExercisesByScope(
      filterExercisesByName(exercises, searchQuery),
      selectedTeamId,
      showBaseLibraryRows,
    ),
    defaultFilter,
  );
  const visibleIds = visible.map((e) => e.id);
  const allVisibleSelected =
    visibleIds.length > 0 &&
    visibleIds.every((id) => selectedExerciseIds.includes(id));
  const someVisibleSelected = visibleIds.some((id) =>
    selectedExerciseIds.includes(id),
  );

  return (
    <Paper className="p-6">
      <Box className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-4">
        <Typography variant="h2">Existing exercises</Typography>
        <Box className="flex flex-wrap items-center gap-2">
          <Button
            variant="outlined"
            onClick={onImport}
            startIcon={<MdUpload />}
          >
            Import
          </Button>
          <Button
            variant="outlined"
            onClick={() => onExport(visibleIds)}
            disabled={visibleIds.length === 0}
          >
            Export
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={onBulkDelete}
            disabled={selectedExerciseIds.length === 0 || isRemoving}
          >
            Remove selected ({selectedExerciseIds.length})
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={onWipe}
            disabled={isRemoving}
          >
            {selectedTeamId ? 'Wipe team library' : 'Wipe org library'}
          </Button>
        </Box>
      </Box>

      <Box className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-end">
        <MuiTextField
          size="small"
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          label="Search exercises"
          placeholder="Filter by exercise name"
        />
        <MuiTextField
          select
          size="small"
          className="min-w-[200px] shrink-0"
          value={defaultFilter}
          aria-label="Filter by default exercises"
          onChange={(event) =>
            setDefaultFilter(event.target.value as DefaultExerciseFilter)
          }
        >
          <MenuItem value="all">All exercises</MenuItem>
          <MenuItem value="default">Default only</MenuItem>
          <MenuItem value="non_default">Non-default only</MenuItem>
        </MuiTextField>
        <FormControlLabel
          className="shrink-0"
          control={
            <Checkbox
              checked={showBaseLibraryRows}
              onChange={(_, checked) => setShowBaseLibraryRows(checked)}
            />
          }
          label="Show base library exercises"
        />
      </Box>

      {removalError && (
        <Alert severity="error" className="mb-3">
          {removalError}
        </Alert>
      )}

      {isLoading ? (
        <Box className="flex justify-center py-12">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer className="max-h-[480px]">
          <Table stickyHeader size="small" aria-label="Existing exercises">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={someVisibleSelected && !allVisibleSelected}
                    checked={allVisibleSelected}
                    onChange={(_, checked) =>
                      onSetAllSelection(visibleIds, checked)
                    }
                    disabled={visibleIds.length === 0}
                    inputProps={{
                      'aria-label': 'Select all visible exercises',
                    }}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Default</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" className="py-8">
                    <Typography variant="body2" className="text-gray-500">
                      {exercises.length === 0
                        ? 'No exercises in this library'
                        : 'No exercises match your filters'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visible.map((exercise) => {
                  const labelId = `existing-exercise-${exercise.id}-label`;
                  const scopeLabel = exercise.teamName ?? 'Base library';
                  return (
                    <TableRow
                      key={exercise.id}
                      hover
                      selected={selectedExerciseIds.includes(exercise.id)}
                      onClick={() => onToggleExerciseSelection(exercise.id)}
                      className="cursor-pointer"
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedExerciseIds.includes(exercise.id)}
                          tabIndex={-1}
                          disableRipple
                          inputProps={{ 'aria-labelledby': labelId }}
                        />
                      </TableCell>
                      <TableCell id={labelId}>{exercise.name}</TableCell>
                      <TableCell>{scopeLabel}</TableCell>
                      <TableCell>
                        {exercise.isDefault ? (
                          <MdCheck
                            aria-label="Default exercise"
                            className="text-gray-600"
                          />
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

export const ExerciseLibraryManagementPage = () => {
  const {
    file,
    exercises,
    isProcessing,
    isImporting,
    validationComplete,
    selectedOrgId,
    selectedTeamId,
    fileInputRef,
    isImportDialogOpen,
    openImportDialog,
    closeImportDialog,
    orgsQuery,
    previewWipeQuery,
    isPreviewNotInListLoading,
    isPreviewNotInListFetching,
    handleOrgChange,
    handleTeamChange,
    handleFileSelect,
    handleDownloadExercises,
    handleImport,
    hasErrors,
    errorCount,
    createCount,
    updateCount,
    skipCount,
    hasExercisesToImport,
    removalMode,
    importRemovalMode,
    setImportRemovalMode,
    candidateExercises,
    selectedExerciseIds,
    toggleExerciseSelection,
    setAllExerciseSelection,
    notInListCandidates,
    notInListCount,
    isConfirmDialogOpen,
    startBulkDelete,
    startWipe,
    startImportConfirm,
    cancelConfirm,
    isRemoving,
    removalError,
    confirmRemoval,
  } = useExerciseLibraryManagement();

  const selectedOrg = orgsQuery.data?.find(
    (org: Org) => org.id === selectedOrgId,
  );

  const selectedTeamName = selectedTeamId
    ? selectedOrg?.teams.find((t: Team) => t.id === selectedTeamId)?.name ??
      null
    : null;

  const orgSettingsQuery = trpc.admin.getOrgSettings.useQuery(
    { orgId: selectedOrgId ?? '' },
    { enabled: !!selectedOrgId },
  );

  const confirmMode: RemovalMode = removalMode ?? 'select_only';
  // Only surface table-level removal errors for the select_only path; import
  // confirm failures stay inside TypeToConfirmDialog.
  const tableRemovalError =
    removalMode === 'select_only' || removalMode === null ? removalError : null;

  return (
    <div className="flex flex-col h-full overflow-auto">
      <PageHeader title="Exercise Library" />
      <div className="flex flex-col gap-6 p-6">
        <Toolbar
          orgs={orgsQuery.data}
          isLoading={orgsQuery.isLoading}
          selectedOrgId={selectedOrgId}
          onOrgChange={handleOrgChange}
          selectedTeamId={selectedTeamId}
          onTeamChange={handleTeamChange}
          teams={selectedOrg?.teams}
          isTeamLibraryEnabled={!!orgSettingsQuery.data?.enableTeamLibrary}
        />

        {selectedTeamId &&
          orgSettingsQuery.data?.enableTeamLibrary &&
          orgSettingsQuery.data?.showBaseLibrary && (
            <Alert severity="info">
              Export downloads exactly the rows shown in the table below. To
              export team exercises separately from base library exercises,
              uncheck &quot;Show base library exercises&quot; first.
            </Alert>
          )}

        {selectedOrgId ? (
          <ExistingExercisesTable
            exercises={candidateExercises}
            isLoading={previewWipeQuery.isLoading}
            selectedTeamId={selectedTeamId}
            selectedExerciseIds={selectedExerciseIds}
            onToggleExerciseSelection={toggleExerciseSelection}
            onSetAllSelection={setAllExerciseSelection}
            onBulkDelete={startBulkDelete}
            onWipe={startWipe}
            onImport={openImportDialog}
            onExport={handleDownloadExercises}
            isRemoving={isRemoving}
            removalError={tableRemovalError}
          />
        ) : (
          <Paper className="p-6">
            <Typography variant="body1" className="text-gray-600">
              Select an organization to view and manage its exercise library.
            </Typography>
          </Paper>
        )}

        <ImportDialog
          open={isImportDialogOpen}
          onClose={closeImportDialog}
          fileInputRef={fileInputRef}
          file={file}
          isProcessing={isProcessing}
          selectedOrgId={selectedOrgId}
          onFileSelect={handleFileSelect}
          validationComplete={validationComplete}
          exercises={exercises}
          hasErrors={hasErrors}
          errorCount={errorCount}
          createCount={createCount}
          updateCount={updateCount}
          skipCount={skipCount}
          hasExercisesToImport={hasExercisesToImport}
          isImporting={isImporting}
          onImport={handleImport}
          requireMetadata={orgSettingsQuery.data?.requireExerciseMetadataFields}
          importRemovalMode={importRemovalMode}
          onImportRemovalModeChange={setImportRemovalMode}
          notInListCandidates={notInListCandidates}
          notInListCount={notInListCount}
          selectedTeamName={selectedTeamName}
          isPreviewNotInListLoading={isPreviewNotInListLoading}
          isPreviewNotInListFetching={isPreviewNotInListFetching}
          onContinueToConfirm={startImportConfirm}
          isConfirmDialogOpen={isConfirmDialogOpen}
          isRemoving={isRemoving}
        />

        {selectedOrg?.name ? (
          <TypeToConfirmDialog
            open={isConfirmDialogOpen}
            orgName={selectedOrg.name}
            mode={confirmMode}
            scopeName={
              removalMode === 'wipe' || removalMode === 'replace'
                ? selectedTeamName
                : null
            }
            onConfirm={confirmRemoval}
            onCancel={cancelConfirm}
            isSubmitting={isRemoving || isImporting}
            error={removalError}
          />
        ) : null}
      </div>
    </div>
  );
};
